import { Extension, GLTF, ReaderContext, WriterContext, vec3, MathUtils } from '@gltf-transform/core';
import { KHR_MATERIALS_TRANSLUCENCY } from '../constants';
import { Translucency } from './translucency';

const NAME = KHR_MATERIALS_TRANSLUCENCY;

interface TranslucencyDef {
	translucencyFactor?: number;
	translucencyColorFactor?: vec3;
	translucencyTexture?: GLTF.ITextureInfo;
	translucencyColorTexture?: GLTF.ITextureInfo;
}

/**
 * # MaterialsTranslucency
 */
export class MaterialsTranslucency extends Extension {
	public readonly extensionName = NAME;
	public static readonly EXTENSION_NAME = NAME;

	/** Creates a new Translucency property for use on a {@link Material}. */
	public createTranslucency(): Translucency {
		return new Translucency(this.document.getGraph());
	}

	/** @hidden */
	public read(context: ReaderContext): this {
		const jsonDoc = context.jsonDoc;
		const materialDefs = jsonDoc.json.materials || [];
		const textureDefs = jsonDoc.json.textures || [];
		materialDefs.forEach((materialDef, materialIndex) => {
			if (materialDef.extensions && materialDef.extensions[NAME]) {
				const translucency = this.createTranslucency();
				context.materials[materialIndex].setExtension(NAME, translucency);

				const translucencyDef = materialDef.extensions[NAME] as TranslucencyDef;

				// Factors.

				if (translucencyDef.translucencyFactor !== undefined) {
					translucency.setTranslucencyFactor(translucencyDef.translucencyFactor);
				}
				if (translucencyDef.translucencyColorFactor !== undefined) {
					translucency.setTranslucencyColorFactor(translucencyDef.translucencyColorFactor);
				}

				// Textures.

				if (translucencyDef.translucencyTexture !== undefined) {
					const textureInfoDef = translucencyDef.translucencyTexture;
					const texture = context.textures[textureDefs[textureInfoDef.index].source!];
					translucency.setTranslucencyTexture(texture);
					context.setTextureInfo(translucency.getTranslucencyTextureInfo()!, textureInfoDef);
				}
				if (translucencyDef.translucencyColorTexture !== undefined) {
					const textureInfoDef = translucencyDef.translucencyColorTexture;
					const texture = context.textures[textureDefs[textureInfoDef.index].source!];
					translucency.setTranslucencyColorTexture(texture);
					context.setTextureInfo(translucency.getTranslucencyColorTextureInfo()!, textureInfoDef);
				}
			}
		});

		return this;
	}

	/** @hidden */
	public write(context: WriterContext): this {
		const jsonDoc = context.jsonDoc;

		this.document
			.getRoot()
			.listMaterials()
			.forEach((material) => {
				const translucency = material.getExtension<Translucency>(NAME);
				if (translucency) {
					const materialIndex = context.materialIndexMap.get(material)!;
					const materialDef = jsonDoc.json.materials![materialIndex];
					materialDef.extensions = materialDef.extensions || {};

					// Factors.

					const translucencyDef = (materialDef.extensions[NAME] = {} as TranslucencyDef);

					if (translucency.getTranslucencyFactor() !== 1) {
						translucencyDef.translucencyFactor = translucency.getTranslucencyFactor();
					}
					if (!MathUtils.eq(translucency.getTranslucencyColorFactor(), [1, 1, 1])) {
						translucencyDef.translucencyColorFactor = translucency.getTranslucencyColorFactor();
					}

					// Textures.

					if (translucency.getTranslucencyTexture()) {
						const texture = translucency.getTranslucencyTexture()!;
						const textureInfo = translucency.getTranslucencyTextureInfo()!;
						translucencyDef.translucencyTexture = context.createTextureInfoDef(texture, textureInfo);
					}
					if (translucency.getTranslucencyColorTexture()) {
						const texture = translucency.getTranslucencyColorTexture()!;
						const textureInfo = translucency.getTranslucencyColorTextureInfo()!;
						translucencyDef.translucencyColorTexture = context.createTextureInfoDef(texture, textureInfo);
					}
				}
			});

		return this;
	}
}
